import clsx from 'clsx'
import styles from './FilterRail.module.css'
import controls from './controls.module.css'
import CountPill from './CountPill'
import GroupHeader from './GroupHeader'
import ScoreControl from './ScoreControl'
import {
  applyParamRef,
  applyRangeRef,
  clearParamGroup,
  type CategoryFilterState,
} from 'lib/dealhunt/filters'
import type { CategoryFilterModel, CategorySummary } from 'lib/dealhunt/types'

interface RatingsPanelProps {
  model: CategoryFilterModel
  summary: CategorySummary
  filters: CategoryFilterState
  onChange: (next: CategoryFilterState) => void
  compact?: boolean
}

/** The SoundGuys block: verdict pills + featured score sliders. Tinted with the
 *  existing dh-info tokens so it reads as editorial, not as another spec group. */
const RatingsPanel: React.VFC<RatingsPanelProps> = ({
  model,
  summary,
  filters,
  onChange,
  compact,
}) => {
  // The backend owns the copy noun ("headphones", "speakers") like every other string
  // in the model; "products" only guards an empty wire value.
  const noun = summary.noun || 'products'
  const blurb =
    summary.labTested > 0
      ? `Filter by what our reviewers measured and scored. ${summary.tested.toLocaleString(
          'en-US'
        )} ${noun} tested in-house, ${summary.labTested.toLocaleString(
          'en-US'
        )} through the MDAQS lab.`
      : `Filter by what our reviewers measured and scored. ${summary.tested.toLocaleString(
          'en-US'
        )} ${noun} tested in-house.`

  return (
    <section
      className={clsx(styles.ratings, { [styles.ratingsCompact]: compact })}
      aria-label="SoundGuys ratings"
    >
      <header className={styles.ratingsHead}>
        <span className={styles.sgMark} aria-hidden="true">
          {'SG'}
        </span>
        <span className={styles.ratingsTitle}>{'SoundGuys ratings'}</span>
      </header>
      {!compact && <p className={styles.ratingsBlurb}>{blurb}</p>}
      <div className={controls.pills} role="group" aria-label="SoundGuys verdict">
        {model.verdict.options.map(opt => (
          <CountPill
            key={opt.value}
            label={opt.label}
            count={opt.count}
            selected={opt.selected}
            onClick={() => onChange(applyParamRef(filters, opt.param, !opt.selected))}
          />
        ))}
      </div>
      {model.scores.map(score => (
        <section
          key={score.id}
          // Stable per-group anchor, same shape as FilterGroupView's.
          id={`filter-group-${score.id}`}
          className={clsx(styles.scoreGroup, { [styles.scoreGroupActive]: Boolean(score.applied) })}
          aria-label={score.label}
        >
          <GroupHeader
            label={score.label}
            active={Boolean(score.applied)}
            onClear={() => onChange(clearParamGroup(filters, score.param))}
          />
          <ScoreControl
            group={score}
            onCommit={min => onChange(applyRangeRef(filters, score.param, min, null))}
          />
        </section>
      ))}
    </section>
  )
}

export default RatingsPanel
